import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createCheckoutSession } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Check, Crown, Sparkles, Zap, CreditCard, CheckCircle2 } from 'lucide-react';
import { logger } from '@/lib/logger';

// Subscription plans without "gratis"
const SUBSCRIPTION_PLANS = {
  company: {
    basis: {
      price: 149.0,
      name: "Basic",
      lookup_key: "company_basic_monthly",
      features: ["Virksomhedsprofil", "20 tilbudsbesvarelser / måned", "Synlig i søgning", "Modtage anmeldelser"],
      quotaLimit: 20,
    },
    pro: {
      price: 399.0,
      name: "Pro",
      lookup_key: "company_pro_monthly",
      features: ["Alt i Basic", "50 tilbudsbesvarelser / måned", "Fremhævet profil", "Bedre placering i søgning", "Statistik over visninger"],
      quotaLimit: 50,
    },
    premium: {
      price: 799.0,
      name: "Premium",
      lookup_key: "company_premium_monthly",
      features: ["Alt i Pro", "Ubegrænsede tilbudsbesvarelser", "Topplacering i søgning", "Verificeret badge"],
      quotaLimit: -1,
    },
  }
};

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('company');

  useEffect(() => {
    if (user?.user_type === 'user') {
      // Users have free access - redirect away from subscription page
      navigate('/', { replace: true });
    } else if (user?.user_type) {
      setActiveTab('company');
    }
  }, [user, navigate]);

  const handleSubscribe = async (planType, tier) => {
    if (!token) {
      toast.error('Log ind for at abonnere');
      navigate('/register-company');
      return;
    }
    if (user?.user_type !== 'company') {
      toast.error('Kun virksomheder kan tegne abonnement');
      return;
    }
    const plan = SUBSCRIPTION_PLANS.company[tier];
    if (!plan?.lookup_key) {
      toast.error('Plan ikke tilgængelig');
      return;
    }
    setLoading(tier);
    try {
      const res = await createCheckoutSession({ lookup_key: plan.lookup_key, origin_url: window.location.origin });
      const url = res?.data?.checkout_url;
      if (!url) {
        toast.error('Kunne ikke hente betalingslink – prøv igen');
        setLoading(null);
        return;
      }
      // Redirect. Reset loading after a beat so if the browser blocks the redirect
      // (popup/CSP/adblock), the user can retry instead of seeing a stuck button.
      window.location.assign(url);
      setTimeout(() => setLoading(null), 4000);
    } catch (e) {
      logger.error('Checkout error:', e);
      const msg = e.response?.data?.detail || e.message || 'Kunne ikke starte betaling';
      toast.error(msg);
      setLoading(null);
    }
  };

  const tierIcons = {
    basis: Zap,
    pro: Sparkles,
    premium: Crown
  };

  const renderPlanCard = (planType, tier, plan) => {
    const Icon = tierIcons[tier];
    const isRecommended = tier === 'pro';
    const isCurrentPlan = user?.subscription_tier === tier && user?.user_type === planType;

    return (
      <Card 
        key={tier}
        className={`subscription-card relative ${isRecommended ? 'popular border-2 border-primary' : 'border-border/50'}`}
        data-testid={`plan-${planType}-${tier}`}
      >
        {isRecommended && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-primary text-primary-foreground px-4 py-1">
              Vi anbefaler
            </Badge>
          </div>
        )}
        
        <CardHeader className="text-center pb-4">
          <div className={`w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-4 ${
            isRecommended ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
          }`}>
            <Icon className="w-6 h-6" />
          </div>
          <CardTitle className="text-xl">{plan.name}</CardTitle>
          <div className="mt-4">
            <span className="text-4xl font-bold">
              {plan.price.toLocaleString('da-DK')} kr
            </span>
            <span className="text-muted-foreground">/måned</span>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4">
          <ul className="space-y-3 mb-6">
            {plan.features.map((feature) => (
              <li key={feature} className="flex items-start gap-3">
                <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isRecommended ? 'text-primary' : 'text-green-600'}`} />
                <span className="text-sm">{feature}</span>
              </li>
            ))}
          </ul>
          
          <Button
            className={`w-full ${isRecommended ? 'bg-primary hover:bg-primary/90' : ''}`}
            variant={isRecommended ? 'default' : 'outline'}
            disabled={isCurrentPlan || loading === tier}
            onClick={() => handleSubscribe(planType, tier)}
            data-testid={`subscribe-${planType}-${tier}`}
          >
            {isCurrentPlan ? 'Nuværende plan' : loading === tier ? (
              <span className="flex items-center gap-2">
                <span className="spinner" />
                Henviser til Stripe...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                Vælg plan
              </span>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Only companies need a subscription; private users get free access.
  const showTabs = !user;
  const relevantPlanType = 'company';

  return (
    <div className="min-h-screen bg-stone-50" data-testid="pricing-page">
      {/* Header */}
      <div className="bg-primary text-white py-16">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            Abonnement
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Vælg den rette plan for dig
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {user?.user_type === 'user'
              ? 'Som privat kunde har du fri adgang. Abonnement er kun for håndværkervirksomheder.'
              : 'Bliv synlig for tusindvis af kunder og modtag tilbudsanmodninger direkte.'}
          </p>
        </div>
      </div>

      {/* Pricing Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        {user?.user_type === 'user' ? (
          <Card className="max-w-xl mx-auto text-center" data-testid="user-free-card">
            <CardContent className="p-10 space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bronze-shine flex items-center justify-center text-white">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold">Du har gratis adgang</h2>
              <p className="text-muted-foreground">
                Som privat kunde betaler du ingenting. Find håndværkere, send tilbudsanmodninger og chat med virksomheder – helt uden abonnement.
              </p>
              <Button onClick={() => navigate('/')} data-testid="user-go-home-button">Gå til forsiden</Button>
            </CardContent>
          </Card>
        ) : showTabs ? (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">For Håndværkervirksomheder</h2>
              <p className="text-sm text-muted-foreground">Som privat kunde behøver du intet abonnement – kun virksomheder betaler.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(SUBSCRIPTION_PLANS.company).map(([tier, plan]) =>
                renderPlanCard('company', tier, plan)
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Virksomhedsplaner</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {Object.entries(SUBSCRIPTION_PLANS[relevantPlanType]).map(([tier, plan]) =>
                renderPlanCard(relevantPlanType, tier, plan)
              )}
            </div>
          </div>
        )}

        {/* Payment Methods */}
        <div className="mt-16 text-center">
          <h3 className="text-lg font-semibold mb-4">Accepterede betalingsmetoder</h3>
          <div className="flex items-center justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2 text-muted-foreground">
              <svg className="w-10 h-6" viewBox="0 0 50 30" fill="currentColor">
                <rect width="50" height="30" rx="4" fill="#1A1F71"/>
                <text x="25" y="20" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">VISA</text>
              </svg>
              <span className="text-sm">Visa</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <svg className="w-10 h-6" viewBox="0 0 50 30" fill="currentColor">
                <rect width="50" height="30" rx="4" fill="#EB001B"/>
                <circle cx="20" cy="15" r="10" fill="#EB001B"/>
                <circle cx="30" cy="15" r="10" fill="#F79E1B"/>
                <path d="M25 8c-2.5 2-4 5-4 7s1.5 5 4 7c2.5-2 4-5 4-7s-1.5-5-4-7z" fill="#FF5F00"/>
              </svg>
              <span className="text-sm">Mastercard</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <svg className="w-10 h-6" viewBox="0 0 50 30" fill="currentColor">
                <rect width="50" height="30" rx="4" fill="#635BFF"/>
                <text x="25" y="18" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold">Stripe</text>
              </svg>
              <span className="text-sm">Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <svg className="w-10 h-6" viewBox="0 0 50 30" fill="currentColor">
                <rect width="50" height="30" rx="4" fill="#5A78FF"/>
                <text x="25" y="18" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold">MobilePay</text>
              </svg>
              <span className="text-sm">MobilePay</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Alle betalinger behandles sikkert via Stripe
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-center mb-8">Ofte stillede spørgsmål</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'Kan jeg skifte plan senere?',
                a: 'Ja, du kan til enhver tid opgradere eller nedgradere din plan. Ændringer træder i kraft ved næste faktureringsperiode.'
              },
              {
                q: 'Er der bindingsperiode?',
                a: 'Nej, alle vores abonnementer er uden binding. Du kan opsige når som helst.'
              },
              {
                q: 'Hvordan fungerer det?',
                a: 'Vælg en plan der passer til dine behov og betal med kort eller MobilePay. Du får adgang med det samme.'
              },
              {
                q: 'Hvad sker der med mine data hvis jeg opsiger?',
                a: 'Dine data gemmes i 30 dage efter opsigelse, så du kan genaktivere din konto hvis du ombestemmer dig.'
              }
            ].map((faq) => (
              <Card key={faq.q}>
                <CardHeader>
                  <CardTitle className="text-base">{faq.q}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
